/* product-highlight-carousel.js
 * <highlight-carousel> — a lightweight scroll-snap image carousel used by the
 * Product highlights section (left-hand "lifestyle" gallery). Dots below the
 * track jump to a slide on click; the active dot updates as the user swipes
 * or scrolls the track manually, via IntersectionObserver rather than scroll
 * math, so it stays correct regardless of image widths/zoom/RTL layout.
 */
class HighlightCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.product-highlights__carousel-track');
    this.slides = Array.from(this.querySelectorAll('.product-highlights__slide'));
    this.dots = Array.from(this.querySelectorAll('.product-highlights__dot'));
    this.prevArrow = this.querySelector('.product-highlights__arrow--prev');
    this.nextArrow = this.querySelector('.product-highlights__arrow--next');

    if (!this.track || this.slides.length < 2) return;

    this.activeIndex = 0;

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToSlide(index));
    });

    if (this.prevArrow) {
      this.prevArrow.addEventListener('click', () => this.goToSlide(this.activeIndex - 1));
    }
    if (this.nextArrow) {
      this.nextArrow.addEventListener('click', () => this.goToSlide(this.activeIndex + 1));
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = this.slides.indexOf(entry.target);
          if (index === -1) return;
          this.setActiveDot(index);
        });
      },
      { root: this.track, threshold: 0.6 }
    );

    this.slides.forEach((slide) => this.observer.observe(slide));
  }

  goToSlide(index) {
    const slide = this.slides[Math.max(0, Math.min(index, this.slides.length - 1))];
    if (!slide) return;
    this.track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
  }

  setActiveDot(index) {
    this.activeIndex = index;
    this.dots.forEach((dot, dotIndex) => {
      dot.classList.toggle('is-active', dotIndex === index);
    });
    if (this.prevArrow) this.prevArrow.disabled = index <= 0;
    if (this.nextArrow) this.nextArrow.disabled = index >= this.slides.length - 1;
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }
}

customElements.define('highlight-carousel', HighlightCarousel);

/* <studio-cycle> — auto-advancing crossfade for the Product highlights
 * section's right-hand "studio" image. Used when the linked product has
 * more than one distinct variant image (e.g. Standard vs Pro); cycles on a
 * timer rather than user interaction, unlike <highlight-carousel>. */
class StudioCycle extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.product-highlights__studio-slide'));
    if (this.slides.length < 2) return;

    this.index = 0;
    this.intervalId = setInterval(() => this.advance(), 2500);

    // Pause while off-screen so it doesn't run forever in a background tab.
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (!this.intervalId) this.intervalId = setInterval(() => this.advance(), 2500);
          } else if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
          }
        });
      },
      { threshold: 0.1 }
    );
    this.observer.observe(this);
  }

  advance() {
    this.slides[this.index].classList.remove('is-active');
    this.index = (this.index + 1) % this.slides.length;
    this.slides[this.index].classList.add('is-active');
  }

  disconnectedCallback() {
    if (this.intervalId) clearInterval(this.intervalId);
    if (this.observer) this.observer.disconnect();
  }
}

customElements.define('studio-cycle', StudioCycle);
